import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material';
import { FormikErrors, FormikValues, useFormik } from 'formik';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import TwCheckbox from '../../components/TwCheckboxes/TwCheckboxes';
import TwDialog from '../../components/TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../components/TwFields/TwFields';
import { ReactComponent as QBOIcon } from '../../assets/icons/QBO-icon.svg';
import TwTooltip from '../../components/TwTooltip/TwTooltip';
import useAuth from '../../providers/Auth/Auth.provider';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import LoadsService from '../../providers/Loads/Loads.service';
import {
  ClientResponse,
  CreateClientData,
  Preferences
} from '../../redux/api/customer/customer.model';
import StringUtils from '../../utils/stringUtils';
import { ReactComponent as Warning } from '../../assets/icons/warning.svg';
import TwDocumentViewer, {
  TwDocumentType
} from '../../components/TwDocumentViewer/TwDocumentViewer';
import './CreateInvoice.scss';
import {
  InvoiceItemV3,
  InvoicePreviewPdfData,
  InvoiceSources,
  InvoiceUpdateDataV3
} from '../../providers/Invoices/Invoices.model';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import BillFrom, { BillFromData } from './BillFrom/BillFrom';
import BillTo, { BillToData, TooltipType } from './BillTo/BillTo';
import TwSelect, { TwSelectOption } from '../../components/TwSelects/TwSelects';
import DateUtils from '../../utils/dateUtils';
import TwDatePicker from '../../components/TwDatePicker/TwDatePicker';
import { RateItem } from './InvoiceItems/InvoiceItems';
import TwTagsField, { TwTagsOption } from '../../components/TwTagsField/TwTagsField';
import UploadBol from './../UploadBol/UploadBol';
import InvoicesService from './../../providers/Invoices/Invoices.service';
import { InvoiceCreateData } from './../../providers/Invoices/Invoices.model';
import { useUpdateCustomerMutation } from '../../redux/api/customer/customerApi';
import { getCustomerContactEmails, getPreviewInvoiceSuggestions } from '../../utils/contactUtils';
import PreviewInvoice, { PreviewInvoiceSendData } from './PreviewInvoice/PreviewInvoice';
import {
  closeCreateInvoice,
  closeLoadDetail,
  closeUploadBol,
  openEditLoad,
  openLoadDetail,
  openUploadBol,
  selectKeepLoadDetailOpen,
  setLoadDetailReload
} from '../../redux/features/navigation/navigationSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setInvoiceDivisionId,
  setInvoiceLoadIds,
  setOrderId
} from '../../redux/features/orders/ordersSlice';
import { LoadDocumentType, LoadRate } from '../../redux/api/load/load.model';
import { useQboCustomerSearchMutation } from '../../redux/api/load/loadApi';
import { RowData } from '../../components/TwAdvancedRates/RateRow';
import { nanoid } from '@reduxjs/toolkit';
import {
  DefaultPaymentTerms,
  InvoiceService,
  InvoiceServiceStatus,
  InvoiceServiceUnit,
  PAYMENT_TERMS
} from '../../redux/api/invoiceService/invoiceService.model';
import { useGetInvoiceServicesQuery } from '../../redux/api/invoiceService/invoiceServiceApi';
import { LoadsRates } from './LoadRates/LoadsRates';
import useInvoiceDefaultDescription from '../../hooks/useInvoiceDefaultDescripion';
import TwSelectableDocsPhotos from '../../components/TwSelectableDocsPhotos/TwSelectableDocsPhotos.';
import FileUtils from '../../utils/fileUtils';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useLocalStorageState from 'use-local-storage-state';
import { DefaultInvoiceDate, UserResponse } from '../../providers/User/User.model';
import { CarrierResponse } from '../../redux/api/carriers/carriers.model';
import { useGetTenantAccountingSettingsQuery } from '../../redux/api/tenant/tenantApi';
import TwQBOLoader from '../../components/TwQBOLoader/TwQBOLoader';
import BillFromQBO from './BillFromQBO/BillFromQBO';
import TwAlert from '../../components/TwAlerts/TwAlerts';
import { ReactComponent as AlertIcon } from '../../assets/alert.svg';
import { Order, OrderAttachment, OrderRate } from '../../redux/api/orders/orders.model';
import TwOrderTripsDocViewer from '../../components/TwOrderTripsDocViewer/TwOrderTripsDocViewer';
import TwUploadOrderTripFiles from '../../components/TwUploadOrderTripFiles/TwUploadOrderTripFiles';
import {
  useCreateInvoiceMutation,
  useGetOrderByIdQuery,
  useGetOrdersForInvoiceMutation
} from '../../redux/api/orders/ordersApi';
import FactorInvoice from './FactorInvoice/FactorInvoice';
import FactoringService from '../../providers/Factoring/Factoring.service';
import { INamedCompanies } from '../../redux/api/factoring/factoring.model';
import FactoringUtils from '../../utils/factoringUtils';
import { tripApi, tripApiUtil } from '../../redux/api/trips/tripApi';
import { TRIPS_ITEMS_PER_PAGE, TRIP_STATUS } from '../../redux/api/trips/trips.model';
import {
  useGetQBOClassesByDivisionQuery,
  useGetQBOClassTrackingQuery
} from '../../redux/api/divisions/divisionsApi';
import { useGetInternalFactoringCompanyQuery } from '../../redux/api/internalFactoring/internalFactoringApi';
import InvoiceUtils from '../../utils/invoiceUtils';
import NumberUtils from '../../utils/numberUtils';
import SalesTaxService, { SalesTax } from '../../providers/Settings/SalesTax.service';
import TwAutocomplete from '../../components/TwAutocomplete/TwAutocomplete';
import SalesTaxCreateUpdateModal from '../Settings/Invoicing/SalesTax/CreateUpdateModal';

export const RATE_REGEX = new RegExp(/^-?\d{1,6}(?:[\.,]\d{1,4})?$/);

export interface CreateInvoiceProps {
  open: boolean;
  loadIds: string[];
  divisionId: string;
  onClickClose: () => void;
  onEditQBOInvoice?: (invoiceId: string, divisionId: string) => void;
}

export interface CreateInvoiceFormValues {
  source: InvoiceSources;
  invoiceItems: RateItem[];
  documents: OrderAttachment[];
  sendInvoice: boolean;
  tenantCcInvoice: boolean;
  companyEmail: string;
  photos: OrderAttachment[];
  paymentTerm: string;
  customDueDate: Date | null;
  specialInstructions: string;
  billFromCompanyId: string;
  qboClassId?: string;
  salesTaxId?: string;
}

export const getCarriersAndCompanyIds = (
  loads: Order[],
  userData?: UserResponse
): { companyIds: string[]; carriers: CarrierResponse[] } => {
  const carriers: CarrierResponse[] = [];
  const companyIds: string[] = [];
  loads.map((load) => {
    load.trips.map((segment) => {
      if (segment.carrier) {
        if (segment?.carrier?.company_id) {
          companyIds.push(segment?.carrier?.company_id);
        }
        carriers.push(segment.carrier);
      }
    });
  });

  if (userData) {
    companyIds.push(userData?.defaultDivision.id);
  }

  return {
    companyIds,
    carriers
  };
};

const BILLING_EMAIL_NOT_FOUND_TEXT =
  'Customer not found in Quickbooks (QBO) by email or display name.\nA new customer will be created in QBO.';
const BILLING_EMAIL_FOUND_TEXT = 'Billing email and/or customer name matched to customer in QBO';

const INITIAL_INVOICE_DATA = {
  source: InvoiceSources.QBO,
  invoiceItems: [],
  documents: [],
  sendInvoice: false, // Default to false, will be updated based on settings
  tenantCcInvoice: true,
  companyEmail: '',
  photos: [],
  paymentTerm: PAYMENT_TERMS[1].value,
  customDueDate: new Date(),
  specialInstructions: '',
  billFromCompanyId: '',
  divisionId: null,
  qboClassId: '',
  salesTaxId: ''
};

export const getErrorInfo = (
  loadsRates: { [key: string]: RowData[] } | undefined,
  source: InvoiceSources | undefined
): [FormikErrors<FormikValues>, string | undefined] => {
  const errors: any = {};

  let errorInputName: string | undefined = undefined;

  const flatRates = Object.values(loadsRates || {})
    .map((loadRates) => loadRates)
    .flat();

  // filter load rates to remove those place holders with no data (i.e., just an ui_id)
  const nonPlaceholderRates = flatRates.filter(
    (r) =>
      r.invoice_service_id ||
      r.qbo_service_id ||
      r.unit ||
      r.rate ||
      (r.unit !== InvoiceServiceUnit.FLAT ? r.quantity : false)
  );

  // advanced rate check
  const flatRatesCheck = flatRates.some(
    (r) =>
      (!r.invoice_service_id && !r.qbo_service_id) ||
      !r.unit ||
      r.rate === null ||
      (r.unit !== InvoiceServiceUnit.FLAT ? !r.quantity : false)
  );

  if (nonPlaceholderRates.length > 0 && flatRatesCheck) {
    const failedInputs = flatRates
      .filter(
        (r) =>
          (!r.invoice_service_id && !r.qbo_service_id) ||
          !r.unit ||
          r.rate === null ||
          (r.unit !== InvoiceServiceUnit.FLAT ? !r.quantity : false)
      )
      .map((fi) => ({
        ui_id: fi.ui_id,
        service: (!fi.invoice_service_id && !fi.qbo_service_id) as boolean,
        unit: !fi.unit,
        rate: fi.rate === null,
        qty: !fi.quantity
      }));

    let inputName = '';
    let keyName = '';

    Object.entries(failedInputs[0]).forEach(([key, value]) => {
      if (inputName) return;

      if (key !== 'ui_id' && value) {
        inputName = `${key}-${failedInputs[0].ui_id}`;
        keyName = key;
      }
    });

    errors[inputName] = `Missing required field: Rate (${
      keyName === 'qty' ? 'quantity' : keyName
    })`;
    errorInputName = inputName;
    return [errors, errorInputName];
  }

  // advanced rate format check: 999999.9999 or -999999.9999
  if (
    flatRates.length > 0 &&
    flatRates.some((r) => r.rate && !RATE_REGEX.test(r.rate.toString()))
  ) {
    const failedInputs = flatRates
      .filter((r) => r.rate && !RATE_REGEX.test(r.rate.toString()))
      .map((fi) => ({
        ui_id: fi.ui_id,
        rate: fi.rate && !RATE_REGEX.test(fi.rate.toString())
      }));

    let inputName = '';
    let keyName = '';

    Object.entries(failedInputs[0]).forEach(([key, value]) => {
      if (inputName) return;

      if (key !== 'ui_id' && value) {
        inputName = `${key}-${failedInputs[0].ui_id}`;
        keyName = key;
      }
    });

    errors[inputName] = `Rate format error: rate must be in 999999.9999 format`;
    errorInputName = inputName;
    return [errors, errorInputName];
  }

  // advanced rate total balance negative check
  if (source === InvoiceSources.QBO && nonPlaceholderRates.length > 0) {
    const totalBalance = flatRates.reduce((acc, r) => {
      if (r.unit === InvoiceServiceUnit.FLAT) {
        return acc + (r.rate || 0);
      }
      return acc + NumberUtils.calculateCurrencyAmount(r.rate, r.quantity);
    }, 0);

    if (totalBalance < 0) {
      errors['total-balance'] = 'Total balance for QBO invoice cannot be negative';
      errorInputName = 'total-balance';
      return [errors, errorInputName];
    }
  }

  return [errors, errorInputName];
};

export const scrollToInput = (inputName: string): void => {
  const input = document.querySelector(`input[name=${inputName}]`);
  input?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'start'
  });
};

// TODO: Recheck this component, needs refactoring
const CreateInvoice: React.FC<CreateInvoiceProps> = ({
  open,
  loadIds,
  divisionId,
  onClickClose,
  onEditQBOInvoice
}): JSX.Element => {
  // hooks
  const dispatch = useAppDispatch();
  const { addNotification, setIsShowing } = useNotifications();
  const { enableSendQboInvoiceInternally, enableZipAttachments, enableFactoring } =
    useFeatureFlags();
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();

  const [mergeAttachments, setMergeAttachments] = useLocalStorageState('mergeAttachments', {
    defaultValue: false
  });
  const [zipAttachments, setZipAttachments] = useLocalStorageState('zipAttachments', {
    defaultValue: false
  });
  const keepDetailOpen = useAppSelector(selectKeepLoadDetailOpen);
  const isUploadBolOpen = useAppSelector((state) => state.navigation?.isUploadBolOpen);
  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const filterParams = useAppSelector((state) => state.activeTrips);

  // #region state
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [haveEmail, setHaveEmail] = useState(false);
  const [oldClientEmail, setOldClientEmail] = useState('');
  const [emailsToSend, setEmailsToSend] = useState<string[]>([]);
  const [emailsToCc, setEmailsToCc] = useState<string[]>([]);
  const [suggestedEmails, setSuggestedEmails] = useState<TwTagsOption[]>([]);
  const [maxAttachmentSize, setMaxAttachmentSize] = useState<number>(35);
  const [emailsToSendQBO, setEmailsToSendQBO] = useState<string[]>([]);
  const [additionalQBOEmails, setAdditionalQBOEmails] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showQBOLoader, setShowQBOLoader] = useState<boolean>(false);
  const [initialInvoiceData, setInitialInvoiceData] =
    useState<CreateInvoiceFormValues>(INITIAL_INVOICE_DATA);
  const [clientData, setClientData] = useState<CreateClientData>();
  const [isOpenWarning, setIsOpenWarning] = useState(false);
  const [blob, setBlob] = useState<Blob>();
  const [invoiceViewerOpen, setInvoiceViewerOpen] = useState(false);
  const [docsViewerOpen, setDocsViewerOpen] = useState(false);
  const [filesToView, setFilesToView] = useState<OrderAttachment[]>([]);
  const [previewBlob, setPreviewBlob] = useState<Blob>();
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [invoicePreviewAttachments, setInvoicePreviewAttachments] = useState<OrderAttachment[]>([]);
  const [selectedFileToView, setSelectedFileToView] = useState<OrderAttachment>(
    {} as OrderAttachment
  );
  const [readableId, setReadableId] = useState<string | number>();
  const [loads, setLoads] = useState<Order[]>([]);
  const [source, setSource] = useState<InvoiceSources | undefined>();
  const [invoiceId, setInvoiceId] = useState<string>('');
  const [qboInvoiceNumber, setQboInvoiceNumber] = useState<string | undefined>(undefined);
  const [tbInvoiceNumber, setTbInvoiceNumber] = useState<string | undefined>(undefined);
  const [billFromData, setBillFromData] = useState<BillFromData | undefined>();
  const [billToData, setBillToData] = useState<BillToData | undefined>();
  const [billFromCompanyIds, setBillFromCompanyIds] = useState<string[]>([]);
  const [billFromSelectedCompanyId, setBillFromSelectedCompanyId] = useState<string | undefined>();
  const [tempBillFromSelectedCompanyId, setTempBillFromSelectedCompanyId] = useState<
    string | undefined
  >();
  const [carriers, setCarriers] = useState<CarrierResponse[]>([]);
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [documentsOpen, setDocumentsOpen] = useState<boolean>(false);
  const [attachmentSizeWarningOpen, setAttachmentSizeWarningOpen] = useState<boolean>(false);
  const [zipAttachmentSizeWarningOpen, setZipAttachmentSizeWarningOpen] = useState<boolean>(false);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [addCustomerEmailToSend, setAddCustomerEmailToSend] = useState<boolean>(false);
  const [loadsRates, setLoadsRates] = useState<{ [key: string]: RowData[] } | undefined>(undefined);
  const [billToTooltipString, setBillToTooltipString] = useState<string>('');
  const [billToTooltipType, setBillToTooltipType] = useState<TooltipType>('info');
  const [showFactorInvoice, setShowFactorInvoice] = useState(false);
  const [factorInvoice, setFactorInvoice] = useState(false);
  const [factoringCompanyName, setFactoringCompanyName] = useState<string | null>(null);
  const [blobTitle, setBlobTitle] = useState<string>('');
  const [downloadName, setDownloadName] = useState<string>('');

  // #region redux
  const {
    data: accountingSettings,
    isFetching: areAccountingSettingsFetching,
    isLoading: areAccountingSettingsLoading
  } = useGetTenantAccountingSettingsQuery(
    {
      divisionId: divisionId
    },
    { skip: !divisionId }
  );
  const [updateClient] = useUpdateCustomerMutation();
  const [createNewInvoice] = useCreateInvoiceMutation();
  const [qboCustomerSearch] = useQboCustomerSearchMutation();
  const [getOrdersForInvoice] = useGetOrdersForInvoiceMutation();
  const { refetch: refetchOrder } = useGetOrderByIdQuery(
    { orderId: loadIds[0] || '' },
    {
      skip: !loadIds[0]
    }
  );
  const { data: fetchedInternalFactoring } = useGetInternalFactoringCompanyQuery(
    { divisionId },
    { skip: !divisionId }
  );

  const { data: qboPreferences = { classTrackingPerTxn: false } } = useGetQBOClassTrackingQuery(
    divisionId,
    { skip: !divisionId }
  );
  const { data: divisionClasses = { data: [], size: 0 } } = useGetQBOClassesByDivisionQuery(
    divisionId,
    { skip: !divisionId }
  );
  const classesDropdown: TwSelectOption[] = [
    { label: 'No Class', value: '' },
    ...divisionClasses.data.map((c) => ({
      label: c.Name,
      value: c.Id
    }))
  ];

  const {
    data: invoiceServicesData,
    isFetching: areInvoiceServicesFetching,
    isLoading: areInvoiceServicesLoading
  } = useGetInvoiceServicesQuery(
    {
      filters: {
        status: [InvoiceServiceStatus.ACTIVE, InvoiceServiceStatus.DISABLED],
        divisionId: divisionId
      }
    },
    {
      skip: !divisionId || !open
    }
  );
  const [invoiceDefaultDescriptionMaker] = useInvoiceDefaultDescription();

  // #region settings
  const isRequireBolActive = getTenantSetting('require-bol', false);
  const isEnabledDriversSeeDocs = getTenantSetting('allow_drivers_to_view_documents');
  const includeNoticeOfAssignmentEnabled = getTenantSetting(
    'enable-factoring-notice-of-assignment'
  );
  const enableDivisions = getTenantSetting('divisions');
  const toCustomerContact = accountingSettings?.to_cust_contact_email;
  const toCustomerBilling = accountingSettings?.to_cust_billing_email;
  const ccTenantEmail = accountingSettings?.cc_user_email;
  const ccTenantAccounting = accountingSettings?.cc_accounting_email;
  const quickbooksEnabled = !!accountingSettings?.division_qbo_credentials?.id;
  const [isMultiLoadInvoice, setIsMultiLoadInvoice] = useState(loadIds.length > 1);
  const isBillFromChanged = tempBillFromSelectedCompanyId !== billFromSelectedCompanyId;
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    setIsMultiLoadInvoice(loadIds.length > 1);
  }, [loadIds]);

  // #region functions
  const getDate30DaysFromNow = (): Date => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date;
  };

  const getDueDateFromSetting = (loads: Order[], daysToAdd: number): Date => {
    const defaultInvoiceDate = accountingSettings?.default_invoice_date;
    let date = new Date();

    if (
      quickbooksEnabled ||
      defaultInvoiceDate === DefaultInvoiceDate.INVOICE_CREATION_DATE ||
      loads.length === 0
    )
      return DateUtils.getDatePlusDays(date, daysToAdd);

    const load = loads[loads.length - 1];

    if (defaultInvoiceDate === DefaultInvoiceDate.LOAD_PICKUP_DATE) {
      const firstStop = load.full_route_stop_overview?.[0];

      if (firstStop) {
        date = DateUtils.getDateInLocale(new Date(firstStop.start_date));
      }
    } else {
      const lastStop = load.full_route_stop_overview?.[load.full_route_stop_overview?.length - 1];

      if (lastStop) {
        date = DateUtils.getDateInLocale(new Date(lastStop.end_date));
      }
    }

    return DateUtils.getDatePlusDays(date, daysToAdd);
  };

  const updateEmailsToSend = (
    customerEmail?: string,
    loadContactEmail?: string,
    preferences?: Preferences
  ): void => {
    // Set default emailsToSend

    const existingEmails = [...emailsToSend];
    const newListToSend = existingEmails.filter((email) => email !== oldClientEmail);

    if (addCustomerEmailToSend && customerEmail) {
      newListToSend.push(customerEmail);
    }

    if (toCustomerContact && loadContactEmail) {
      newListToSend.push(loadContactEmail);
    }

    // Set default emailsToCc for Truckbase invoice Preview
    const listToCc = [];

    if (ccTenantAccounting && accountingSettings?.accounting_email) {
      listToCc.push(accountingSettings.accounting_email);
    }

    if (ccTenantEmail && userData?.email_address) {
      listToCc.push(userData.email_address);
    }

    // if customer has preferences, add additional recipients from customer preferences
    if (preferences) {
      const emailsFromPreferences = preferences?.invoice_settings?.additional_invoice_recipients;

      emailsFromPreferences &&
        emailsFromPreferences.forEach((email) => {
          listToCc.push(email);
        });
    } else {
      // if customer has NO preferences, add additional recipients from global division accounting settings

      if (accountingSettings && accountingSettings.additional_recipients) {
        accountingSettings.additional_recipients.forEach((email) => {
          listToCc.push(email);
        });
      }
    }

    // TB recipients
    setEmailsToSend(newListToSend);
    setEmailsToCc(Array.from(new Set(listToCc)));

    // QBO recipients
    customerEmail && setEmailsToSendQBO([customerEmail]); // Only QBO will use just customer billing email always (regardless of settings)
    setAdditionalQBOEmails(listToCc);
    if (source === InvoiceSources.QBO && toCustomerContact) {
      if (loadContactEmail) {
        setAdditionalQBOEmails((prev) => [...prev, loadContactEmail]);
      }
    }
  };

  const checkEmail = (customer: ClientResponse): void => {
    setHaveEmail(!!customer.billing_email);

    setOldClientEmail(customer.billing_email || '');
  };

  // TODO: Needs re approach (probably children component calling useLoadByIdQuery hook)
  // Can't migrate this to RTK Query
  const getLoadsData = useCallback(async () => {
    try {
      if (!loadIds || loadIds.length === 0 || !accountingSettings || !invoiceServicesData) return;

      const getInvoiceDefaultDescription = invoiceDefaultDescriptionMaker(accountingSettings);
      let invoicePhotos: OrderAttachment[] = [];
      let onlyDocs: OrderAttachment[] = [];

      const loads = await getOrdersForInvoice(loadIds).unwrap();

      if (loads && loads.length > 0) {
        // for single load invoice its the only load
        // for multiload invoice we use first load to populate common data like customers
        const load = loads[0];

        setReadableId(load.readable_id);

        setClientData({
          companyName: load.raw_customer?.name || '',
          companyId: load.raw_customer?.id,
          billingEmail: load.raw_customer?.billing_email
        });

        // set payment terms and additional recipients if customer has preferences
        let paymentTermFromPreferences = PAYMENT_TERMS[1].value;
        if (load.raw_customer?.preferences) {
          paymentTermFromPreferences =
            load.raw_customer.preferences.invoice_settings.default_payment_terms;
          setFieldValue('paymentTerm', paymentTermFromPreferences);

          if (paymentTermFromPreferences !== DefaultPaymentTerms.OTHER) {
            setDueDate(
              getDueDateFromSetting(
                loads,
                InvoiceUtils.getDaysFromPaymentTerms(
                  paymentTermFromPreferences as DefaultPaymentTerms
                )
              )
            );
          }
        } else {
          setDueDate(getDueDateFromSetting(loads, 30));
        }

        // Check customer email from load
        checkEmail(load.raw_customer!);

        // Set customer contacts emails to use as suggestions
        if (source === InvoiceSources.QBO) {
          setSuggestedEmails(
            getCustomerContactEmails(
              load.raw_customer!,
              includeNoticeOfAssignmentEnabled,
              userData?.factoringCompany
            )
          );
        } else {
          setSuggestedEmails(
            getPreviewInvoiceSuggestions(
              load.raw_customer!,
              userData,
              includeNoticeOfAssignmentEnabled
            )
          );
        }
        const allDocs = await LoadsService.getAllDocumentsByOrderOrTrip(loadIds);

        if (allDocs && allDocs.length > 0) {
          invoicePhotos = allDocs.filter(
            (doc) =>
              doc.attachment.file_type.includes('image') &&
              (!doc.type || doc.type === LoadDocumentType.empty)
          );
          onlyDocs = allDocs.filter(
            (doc) =>
              doc.type !== LoadDocumentType.empty || !doc.attachment.file_type.includes('image')
          );

          invoicePhotos = await FileUtils.getPhotosInBatch(invoicePhotos, 5);
        }

        setInitialInvoiceData({
          ...initialInvoiceData,
          sendInvoice: quickbooksEnabled
            ? false
            : accountingSettings.select_invoice_send_option !== undefined
            ? accountingSettings.select_invoice_send_option
            : true, // Use tenant's setting if set, otherwise default to true only if for TB invoices
          photos: invoicePhotos,
          documents: onlyDocs,
          paymentTerm: paymentTermFromPreferences,
          // Just to calm down TS
          // By the point in time this is executed there will be a source already...
          invoiceItems: getDefaultLineItems(loads)
        });
        const loadRatesMap: { [key: string]: RowData[] } = {};
        loads.forEach((load) => {
          if (load?.order_rates && load?.order_rates.length > 0) {
            // if advanced load rates present
            const formatRates =
              load?.order_rates.map((rate, servicesIndex) => {
                // we check if the service coming from the load is present in the invoice services
                // E.g Creating an invoice for a load with Truckbase services while connected to QBO or vice versa
                const service = invoiceServicesData?.content?.find((service) =>
                  quickbooksEnabled
                    ? service.id === rate.qbo_service_id
                    : service.id === rate.invoice_service_id
                );

                const isServiceOk = !!service;

                // Form the rate object
                const r = {
                  ...rate,
                  // calculate the amount with precision to see to 4 decimal points on the row level
                  amount: NumberUtils.calculateCurrencyAmountWithPrecision(
                    rate.rate,
                    rate.quantity
                  ),
                  ui_id: nanoid(),
                  initial_service_option: {
                    id: quickbooksEnabled ? rate.qbo_service_id! : rate.invoice_service_id!,
                    label: isServiceOk ? rate.service_name! : '',
                    value: {
                      id: quickbooksEnabled ? rate.qbo_service_id! : rate.invoice_service_id!,
                      name: isServiceOk ? rate.service_name : '',
                      default_rate: 0,
                      unit: rate.unit
                    } as InvoiceService
                  },
                  description: servicesIndex === 0 ? getInvoiceDefaultDescription(load) : ''
                };

                // if service is not present in invoice services, clear out to force the user to select a service

                if (!isServiceOk) {
                  r.service_name = '';
                  r.invoice_service_id = undefined;
                  r.qbo_service_id = undefined;
                }

                return r;
              }) || [];

            loadRatesMap[load.id] = formatRates.sort((a, b) => a.sort_order - b.sort_order);
          } else {
            // if simple load rate present

            if (!quickbooksEnabled) {
              // set default service
              let service = invoiceServicesData?.content?.find((service) => service.isDefault);

              // if no default service present use flat rate service
              if (!service) {
                service = invoiceServicesData?.content?.find(
                  (service) => service.name === 'Linehaul flat rate'
                );
              }

              if (service) {
                loadRatesMap[load.id] = [
                  {
                    invoice_service_id: service.id,
                    amount: NumberUtils.calculateCurrencyAmount(load.rate, 1),
                    unit: service.unit,
                    quantity: 1,
                    rate: load.rate,
                    load_id: load.id,
                    ui_id: nanoid(),
                    service_name: service.name,
                    initial_service_option: {
                      id: service.id,
                      label: service.name,
                      value: {
                        id: service.id,
                        name: service.name,
                        default_rate: 0,
                        unit: service.unit
                      } as InvoiceService
                    },
                    description: getInvoiceDefaultDescription(load)
                  }
                ];
              }
            } else {
              // set default service
              let service = invoiceServicesData?.content?.find((service) => service.isDefault);

              // if no default service present use Services
              if (!service) {
                service = invoiceServicesData?.content?.find(
                  (service) => service.name === 'Services'
                );
              }

              const uiId = nanoid();
              loadRatesMap[load.id] = [
                {
                  qbo_service_id: service ? service.id : undefined,
                  qbo_service_type: service ? service.type : undefined,
                  amount: NumberUtils.calculateCurrencyAmount(load.rate, 1),
                  unit: service?.isDefault ? InvoiceServiceUnit.FLAT : service?.unit,
                  quantity: 1,
                  rate: load.rate,
                  load_id: load.id,
                  ui_id: uiId,
                  service_name: service?.name || 'Services',
                  initial_service_option: {
                    id: service ? service.id : uiId,
                    label: service?.name || 'Services',
                    value: {
                      id: service ? service.id : uiId,
                      name: service?.name || 'Services',
                      default_rate: 0,
                      unit: InvoiceServiceUnit.FLAT
                    } as InvoiceService
                  },
                  description: getInvoiceDefaultDescription(load)
                }
              ];
            }
          }
        });
        setLoadsRates(loadRatesMap);

        setLoads(loads);
      }
    } catch (error) {
      console.error(error);
      logRocketTrack('Create Invoice', {
        action: 'Create invoice for a load',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: `Couldn't get order info.`,
        variant: 'error'
      });
    }
  }, [JSON.stringify(loadIds.map((id) => id)), accountingSettings, invoiceServicesData]);

  const getDefaultLineItems = (loads: Order[]): RateItem[] => {
    if (!accountingSettings) return [];

    const getInvoiceDefaultDescription = invoiceDefaultDescriptionMaker(accountingSettings);

    return loads.map((load, index) => {
      return {
        product: null,
        amount: load?.rate as number,
        description: getInvoiceDefaultDescription(load),
        order: index
      };
    });
  };

  const saveBillFromInfo = (billFromData: BillFromData): void => {
    setBillFromData(billFromData);
  };

  const saveBillToInfo = (billToInfo: BillToData): void => {
    setBillToData({ ...billToInfo, withFactoringAssignment: !!includeNoticeOfAssignmentEnabled });
  };

  const createInvoice = async (
    values: CreateInvoiceFormValues,
    previewData?: PreviewInvoiceSendData
  ): Promise<void> => {
    if (previewData) {
      addNotification({
        message: factorInvoice
          ? 'Invoice is being sent to factoring company...'
          : 'Invoice is being created...',
        variant: 'loading',
        timeToShow: 10000
      });
    }
    const invoiceItems: InvoiceItemV3[] = loadsRates
      ? Object.keys(loadsRates)
          .map((loadId) => {
            const loadRates = loadsRates[loadId];
            return loadRates.map((rate, index) => ({
              description: rate.description || '',
              amount: rate.amount || 0,
              invoice_service_id: !quickbooksEnabled ? rate.invoice_service_id : undefined,
              invoice_service_name: rate.service_name || '',
              qbo_service_id: quickbooksEnabled ? rate.qbo_service_id : undefined,
              qbo_service_type: quickbooksEnabled ? rate.qbo_service_type : undefined,
              unit: rate.unit || undefined,
              rate: rate.rate || undefined,
              quantity: rate.quantity,
              order_id: loadId,
              sort_order: index + 1,
              qbo_attributes: rate.qbo_attributes
            }));
          })
          .flat()
      : [];
    let skipFinally = false;
    try {
      const invoice: InvoiceCreateData = {
        orderIds: loadIds,
        clientId: clientData?.companyId,
        source: source ? source : InvoiceSources.TB,
        sendInvoice: values.sendInvoice,
        sendTo: source === InvoiceSources.QBO ? emailsToSendQBO : emailsToSend ?? [],
        ccList: source === InvoiceSources.QBO ? additionalQBOEmails : emailsToCc ?? [],
        invoiceItems: invoiceItems,
        invoiceAttachments: selectedDocs,
        invoicePhotos: selectedPhotos,
        tenantCC: values.tenantCcInvoice,
        specialInstructions: values.specialInstructions,
        dueDate: dueDate?.toISOString(),
        withFactoringAssignment: billToData?.withFactoringAssignment,
        mergeAttachments: mergeAttachments,
        zipAttachments: zipAttachments,
        billFromCompanyId: values.billFromCompanyId,
        divisionId,
        tripOrderAttachmentIds: [...selectedPhotos, ...selectedDocs]
      };
      if (quickbooksEnabled) {
        if (enableDivisions) {
          invoice.billFromCompanyId = divisionId;
          invoice.divisionId = divisionId;
        } else {
          invoice.billFromCompanyId = userData?.tenantAccountingSettings?.division_id || '';
          invoice.divisionId = userData?.tenantAccountingSettings?.division_id || '';
        }
      } else {
        invoice.billFromCompanyId = values.billFromCompanyId;
        invoice.divisionId = divisionId;
      }
      if (previewData) {
        invoice.emailSubject = previewData.emailSubject;
        invoice.emailBody = previewData.emailBody;
      }
      if (values.qboClassId) invoice.qboClassId = values.qboClassId;
      if (salesTax) invoice.salesTaxId = salesTax.id;

      let invoiceId;
      let invoiceCreatedId: null | string = null;
      let invoiceResponse;
      let hasOrder = true;
      if (factorInvoice && !isMultiLoadInvoice) {
        try {
          const responseFactoring = await FactoringService.postExport(invoice);

          addNotification({
            message: `Invoice(s) sent to ${FactoringUtils.capitalizeFirstLetterCompany(
              factoringCompanyName || undefined
            )} for factoring`,
            variant: 'success',
            timeToShow: 5000,
            onClosed: () => setIsFirstTime(true)
          });

          const { createdInvoices } = responseFactoring;
          if (createdInvoices.length > 0) {
            invoiceId = createdInvoices[0].invoice_id;
            if (!createdInvoices[0].id) {
              hasOrder = false;
              return;
            }

            invoiceCreatedId = createdInvoices[0].id;
          }
        } catch (err: any) {
          const message = err.error ? err.error : 'Error sending invoice to factoring company';
          addNotification({
            message,
            variant: 'error'
          });
        }
      } else {
        // create invoice
        // Analyze load status to determine which statuses to update in Active Loads
        // (update cached load data for old and new statuses)
        // const { prevStatus, newStatus } = getPrevAndNewStatuses(loads[0].status, LoadStatus.invoiced);
        invoiceResponse = await createNewInvoice({
          data: {
            invoiceData: invoice
          },
          searchTripParams: filterParams
        }).unwrap();

        // Invalidate active trips to refresh the list
        // this is needed because the order status change could affect the trips
        // that are shown in the active trips list, so we need to refresh it since page 1 or offset 0
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...filterParams?.filters,
              status: [TRIP_STATUS.DISPATCHED]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...filterParams?.filters,
              status: [TRIP_STATUS.DISPATCHED]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...filterParams?.filters,
              status: [TRIP_STATUS.IN_TRANSIT]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...filterParams?.filters,
              status: [TRIP_STATUS.DELIVERED]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(tripApiUtil.invalidateTags(['ActiveTrips']));

        invoiceCreatedId = invoiceResponse?.data?.id || '';
        invoiceId = invoiceResponse?.data?.invoice_id;
      }

      if (factorInvoice) {
        try {
          await refetchOrder();
        } catch (error) {
          console.error(error);
        }
      }

      setTbInvoiceNumber(invoiceId || '');
      setInvoiceId(invoiceCreatedId!);
      // get blob of invoice
      const invoiceBlob = await InvoicesService.getInvoicePdf(invoiceCreatedId!);
      setBlob(invoiceBlob);
      dispatch(setOrderId(loadIds[0]));
      if (source === InvoiceSources.QBO && enableSendQboInvoiceInternally && hasOrder) {
        setQboInvoiceNumber(invoiceId || '');
        // Preview QBO invoice
        skipFinally = true;
        dispatch(closeLoadDetail());
        setInvoicePreviewOpen(true);
        setInvoicePreviewLoading(true);
        if (invoiceBlob) setPreviewBlob(invoiceBlob);
        setShowQBOLoader(false);
        setIsShowing(false);
        setInvoicePreviewLoading(false);
        return;
      }
      // refresh other components if necessary
      if (isLoadDetailOpen) {
        dispatch(setLoadDetailReload(true));
      } else {
        dispatch(openLoadDetail());
        dispatch(setLoadDetailReload(true));
      }

      if (invoiceResponse) {
        setBlobTitle(InvoiceUtils.getInvoiceBlobTitle(invoiceResponse.data.invoice_id || ''));
        setDownloadName(InvoiceUtils.getInvoiceDownloadName(invoiceResponse.data.invoice_id || ''));
        // TODO fix this, since using redux this invoiceResponse does not include the actual status code!
        addNotification({
          message: invoiceResponse.message,
          variant: invoiceResponse.status === 206 ? 'error' : 'success',
          timeToShow: 5000,
          onClosed: () => setIsFirstTime(true),
          actionOptions: {
            action: 'open',
            onActionClick: (): void => {
              // Opens invoice PDF viewer
              setInvoiceViewerOpen(true);
            }
          }
        });
      }
    } catch (err: any) {
      console.error('Invoice error', err);
      addNotification({
        message: err?.data?.message
          ? err?.data?.message
          : 'Invoice error. Contact support@truckbase.com.',
        variant: 'error',
        actionOptions: {
          action: 'RETRY',
          onActionClick: (): void => {
            sendInvoice(values);
          }
        },
        timeToShow: 5000,
        onClosed: () => setIsFirstTime(true)
      });
      setShowQBOLoader(false);
    } finally {
      if (skipFinally) return;
      handlePreviewInvoiceClose(true);
      handleClose();

      if (factorInvoice) handleSavePreview();
    }
  };

  const previewInvoice = async (values: CreateInvoiceFormValues): Promise<void> => {
    const invoiceItems: InvoiceItemV3[] = loadsRates
      ? Object.keys(loadsRates)
          .map((loadId) => {
            const loadRates = loadsRates[loadId];
            return loadRates.map((rate, index) => ({
              description: rate.description || '',
              amount: rate.amount || 0,
              invoice_service_id: !quickbooksEnabled ? rate.invoice_service_id : undefined,
              invoice_service_name: rate.service_name || '',
              qbo_service_id: quickbooksEnabled ? rate.qbo_service_id : undefined,
              qbo_service_type: quickbooksEnabled ? rate.qbo_service_type : undefined,
              unit: rate.unit || undefined,
              rate: rate.rate || undefined,
              quantity: rate.quantity,
              order_id: loadId,
              sort_order: index + 1
            }));
          })
          .flat()
      : [];

    const checkedPhotos = selectedPhotos.map(
      (photo) => values.photos.find((value) => value.id === photo) || ({} as OrderAttachment)
    );

    const checkedDocuments = selectedDocs.map(
      (doc) => values.documents.find((value) => value.id === doc) || ({} as OrderAttachment)
    );

    setInvoicePreviewAttachments([...checkedPhotos, ...checkedDocuments]);

    setInvoicePreviewOpen(true);
    setInvoicePreviewLoading(true);
    setTbInvoiceNumber(
      accountingSettings?.invoice_prefix !== null
        ? `${accountingSettings?.invoice_prefix}-[INVOICE_NUMBER]`
        : '[INVOICE_NUMBER]'
    );

    try {
      const invoice: InvoiceCreateData = {
        orderIds: loadIds,
        clientId: clientData?.companyId,
        source: source ? source : InvoiceSources.TB,
        sendInvoice: values.sendInvoice,
        sendTo: emailsToSend ? emailsToSend : [],
        invoiceItems: invoiceItems,
        invoiceAttachments: selectedDocs,
        invoicePhotos: selectedPhotos,
        tenantCC: values.tenantCcInvoice,
        specialInstructions: values.specialInstructions,
        dueDate: dueDate?.toISOString(),
        withFactoringAssignment: billToData?.withFactoringAssignment,
        mergeAttachments: mergeAttachments,
        zipAttachments: zipAttachments,
        billFromCompanyId: values.billFromCompanyId,
        divisionId,
        tripOrderAttachmentIds: [...selectedPhotos, ...selectedDocs]
      };
      // Generate preview blob from invoice

      const subTotal = loadsRates ? getTotal(loadsRates) : 0;
      const salesTaxAmount = totalWithTax - subTotal;
      const salesTaxRate = salesTax?.value || 0;
      const salesTaxName = salesTax?.label || '';

      const invoicePreviewData: InvoicePreviewPdfData = {
        invoice: invoice,
        salesTaxEnabled: isSalesTaxEnabled,
        salesTaxRate: salesTaxRate,
        subtotal: subTotal,
        salesTaxAmount: salesTaxAmount,
        amount: totalWithTax,
        salesTaxName: salesTaxName
      };

      const invoicePreviewResponse = await InvoicesService.generateInvoicePreviewV3(
        invoicePreviewData
      );

      // Set blob of invoice preview
      invoicePreviewResponse && setPreviewBlob(invoicePreviewResponse);
      setIsShowing(false);
    } catch (err: any) {
      console.error('Invoice preview error', err);
      setTbInvoiceNumber('');
      setIsShowing(false);
      addNotification({
        message: err.message ? err.message : `Couldn't generate preview`,
        variant: 'error',
        actionOptions: {
          action: 'RETRY',
          onActionClick: (): void => {
            sendInvoice(values);
          }
        },
        timeToShow: 5000,
        onClosed: () => setIsFirstTime(true)
      });
    } finally {
      setInvoicePreviewLoading(false);
    }
  };

  const sendInvoice = async (values: CreateInvoiceFormValues): Promise<void> => {
    // --- Validations ---
    const checkedDocuments = selectedDocs.map(
      (doc) => values.documents.find((value) => value.id === doc) || ({} as OrderAttachment)
    );
    const totalDocsSize = checkedDocuments.reduce((acc, doc) => acc + doc.attachment.size, 0);

    const checkedPhotos = selectedPhotos.map(
      (photo) => values.photos.find((value) => value.id === photo) || ({} as OrderAttachment)
    );
    const totalPhotosSize = checkedPhotos.reduce((acc, ph) => acc + ph.attachment.size, 0);

    const totalFilesSize = totalDocsSize + totalPhotosSize;

    if (totalFilesSize >= maxAttachmentSize * 1024 * 1024) {
      // 35 MB if not zipped, 70 MB if zipped
      if (zipAttachments) {
        setZipAttachmentSizeWarningOpen(true);
        return;
      } else {
        setAttachmentSizeWarningOpen(true);
        return;
      }
    }

    // Handling empty Bill from data errors
    if (source === InvoiceSources.TB && !billFromData?.companyName) {
      logRocketTrack('Create Invoice', {
        action: 'Populate bill from info',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: `Please fill in your company name.`,
        variant: 'error',
        timeToShow: 7000
      });

      return;
    }

    // Handling invalid added/edited email in Bill to section
    if (billToData?.clientEmail && !StringUtils.isValidEmail(billToData?.clientEmail)) {
      logRocketTrack('Create Invoice', {
        action: 'Populate bill to info',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: `Error: customer contact email is invalid.`,
        variant: 'error',
        timeToShow: 5000
      });

      return;
    }

    // --- End Validations ---

    // New QBO flow
    if (source === InvoiceSources.QBO && enableSendQboInvoiceInternally) {
      // Prevent creating invoice if custom field is more than 31 characters long
      if (
        loads[0]?.customer_order_number?.length > 31 &&
        !!accountingSettings?.qbo_customer_number_custom_field
      ) {
        addNotification({
          message: `Quickbooks custom field maximum length is 31 and Order #${loads[0]?.readable_id} "Customer order #" exceeds that limit.`,
          variant: 'error',
          timeToShow: 7000
        });
        return;
      }

      setShowQBOLoader(true);

      try {
        createInvoice(values);
      } catch (err: any) {
        addNotification({
          message: err.message ? err.message : 'Invoice error. Contact support@truckbase.com.',
          variant: 'error',
          actionOptions: {
            action: 'RETRY',
            onActionClick: (): void => {
              sendInvoice(values);
            }
          },
          timeToShow: 5000,
          onClosed: () => setIsFirstTime(true)
        });
      }

      return;
    }

    if (source === InvoiceSources.QBO && !enableSendQboInvoiceInternally) {
      if (additionalQBOEmails.length <= 0 && values.sendInvoice && emailsToSendQBO.length <= 0) {
        addNotification({
          message: 'Please fill in the Additional recipients field with at least one email',
          variant: 'error'
        });
        return;
      }
    }
    /* If send invoice is CHECKED we want to make customer updates before 
    trying to create the invoice */
    if (values.sendInvoice) {
      addNotification({
        message:
          source === InvoiceSources.TB
            ? 'Generating invoice preview...'
            : 'Invoice is being created...',
        variant: 'loading',
        timeToShow: 10000
      });

      try {
        // QBO Invoice - Client email update
        if (source === InvoiceSources.QBO && !haveEmail && values.companyEmail) {
          try {
            const params = {
              ...clientData,
              externalCustomerId: clientData?.externalCustomerId,
              companyId: clientData?.companyId ?? '',
              companyEmail: values.companyEmail,
              companyName: clientData?.companyName ?? ''
            };
            updateClient(params)
              .unwrap()
              .catch((error) => {
                console.error(error);
                logRocketTrack('Create Invoice', {
                  action: 'Edit bill to info',
                  severity: 'high',
                  tenant_id: userData?.tenant?.id,
                  company_name: userData?.tenant?.company_name,
                  email: userData?.email_address,
                  user_first_name: userData?.first_name,
                  user_last_name: userData?.last_name
                });
                addNotification({
                  message: 'Error at updating customer info, please try again',
                  variant: 'error'
                });
              });
          } catch (error: any) {
            console.error(error);
            addNotification({
              message: error.message,
              variant: 'error'
            });
            // We stop process and let the user fix any errors in the email
            return;
          }
          dispatch(closeCreateInvoice());
        }

        source === InvoiceSources.TB ? previewInvoice(values) : createInvoice(values);
      } catch (err: any) {
        console.error(err);
        setIsShowing(false);
        addNotification({
          message: err.message ? err.message : 'Invoice error. Contact support@truckbase.com.',
          variant: 'error',
          actionOptions: {
            action: 'RETRY',
            onActionClick: (): void => {
              sendInvoice(values);
            }
          },
          timeToShow: 5000,
          onClosed: () => setIsFirstTime(true)
        });
      }

      /* If send invoice is NOT checked we want to start creating the invoice 
    and at the end update customer information. */
    } else {
      addNotification({
        message: 'Invoice is being created...',
        variant: 'loading',
        timeToShow: 10000
      });

      // We close the Create invoice dialog
      dispatch(closeCreateInvoice());

      try {
        if (source === InvoiceSources.TB && values.sendInvoice) {
          previewInvoice(values);
        } else {
          createInvoice(values);
        }

        // QBO Invoice - Client email update
        if (source === InvoiceSources.QBO && !haveEmail && values.companyEmail) {
          try {
            const params = {
              ...clientData,
              externalCustomerId: clientData?.externalCustomerId,
              companyEmail: values.companyEmail,
              companyName: clientData?.companyName ?? '',
              companyId: clientData?.companyId ?? ''
            };
            await updateClient(params);
          } catch (error: any) {
            console.error(error);
            addNotification({
              message: error.message,
              variant: 'error'
            });
          }
        }
      } catch (err: any) {
        console.error(err);
        addNotification({
          message: err.message ? err.message : 'Invoice error. Contact support@truckbase.com.',
          variant: 'error',
          actionOptions: {
            action: 'RETRY',
            onActionClick: (): void => {
              sendInvoice(values);
            }
          },
          timeToShow: 5000,
          onClosed: () => setIsFirstTime(true)
        });
      } finally {
        dispatch(setOrderId(undefined));
        handleClose();
      }
    }
  };

  const sendQBOInvoice = async (
    invoiceId: string,
    previewData?: PreviewInvoiceSendData
  ): Promise<void> => {
    // It shouldn't be open at this point but somehow it happened in prod
    setShowQBOLoader(false);

    try {
      addNotification({
        message: 'Invoice is being sent...',
        variant: 'loading',
        timeToShow: 10000
      });

      if (!haveEmail && values.companyEmail) {
        try {
          const params = {
            ...clientData,
            externalCustomerId: clientData?.externalCustomerId,
            companyId: clientData?.companyId ?? '',
            companyEmail: values.companyEmail,
            companyName: clientData?.companyName ?? ''
          };
          updateClient(params)
            .unwrap()
            .catch((error) => {
              console.error(error);
              logRocketTrack('Create Invoice', {
                action: 'Edit bill to info',
                severity: 'high',
                tenant_id: userData?.tenant?.id,
                company_name: userData?.tenant?.company_name,
                email: userData?.email_address,
                user_first_name: userData?.first_name,
                user_last_name: userData?.last_name
              });
              addNotification({
                message: 'Error at updating customer info, please try again',
                variant: 'error'
              });
            });
        } catch (error: any) {
          console.log(error);
          addNotification({
            message: error.message,
            variant: 'error'
          });
          // We stop process and let the user fix any errors in the email
          return;
        }
        dispatch(closeCreateInvoice());
      }

      const invoiceItems: InvoiceItemV3[] = loadsRates
        ? Object.keys(loadsRates)
            .map((loadId) => {
              const loadRates = loadsRates[loadId];
              return loadRates.map((rate, index) => ({
                description: rate.description || '',
                amount: rate.amount || 0,
                invoice_service_id: !quickbooksEnabled ? rate.invoice_service_id : undefined,
                invoice_service_name: rate.service_name || '',
                qbo_service_id: quickbooksEnabled ? rate.qbo_service_id : undefined,
                qbo_service_type: quickbooksEnabled ? rate.qbo_service_type : undefined,
                unit: rate.unit || undefined,
                rate: rate.rate || undefined,
                quantity: rate.quantity,
                order_id: loadId,
                sort_order: index + 1,
                qbo_attributes: rate.qbo_attributes
              }));
            })
            .flat()
        : [];

      const invoice: InvoiceUpdateDataV3 = {
        sendInvoice: true,
        sendTo: emailsToSend ? emailsToSend : [],
        invoiceItems: invoiceItems,
        invoiceAttachments: selectedDocs,
        invoicePhotos: selectedPhotos,
        tenantCC: values.tenantCcInvoice,
        specialInstructions: values.specialInstructions,
        dueDate: dueDate?.toISOString(),
        mergeAttachments: mergeAttachments,
        zipAttachments: zipAttachments,
        billFromCompanyId: billFromSelectedCompanyId ? billFromSelectedCompanyId : null,
        sendAsCreated: true,
        divisionId,
        // Sending invoice for QBO from preview page so invoice with all latest attachments is already created so no need to update attachments again
        skipAttachmentUpdates: true
      };

      if (values.qboClassId) {
        const classList = divisionClasses.data;
        const index = classList.findIndex((c) => c.Id === values.qboClassId);
        invoice.qboClassRef = {
          value: classList[index].Id,
          name: classList[index].Name
        };
      }
      if (previewData) {
        invoice.emailSubject = previewData.emailSubject;
        invoice.emailBody = previewData.emailBody;
        invoice.ccList = previewData.ccList;
      }

      const invoiceResponse = await InvoicesService.updateInvoice(invoiceId, invoice);

      // get blob of invoice
      const invoiceBlob = await InvoicesService.getInvoicePdf(invoiceId);
      setBlob(invoiceBlob);

      dispatch(closeCreateInvoice());

      if (isLoadDetailOpen) {
        dispatch(setLoadDetailReload(true));
      } else {
        dispatch(openLoadDetail());
        dispatch(setLoadDetailReload(true));
      }

      addNotification({
        message: invoiceResponse.message,
        variant: invoiceResponse.status === 206 ? 'error' : 'success',
        timeToShow: 5000,
        onClosed: () => setIsFirstTime(true),
        actionOptions: {
          action: 'open',
          onActionClick: (): void => {
            // Opens invoice PDF preview
            setInvoiceViewerOpen(true);
          }
        }
      });
    } catch (err: any) {
      addNotification({
        message: err.message ? err.message : 'Invoice error. Contact support@truckbase.com.',
        variant: 'error',
        actionOptions: {
          action: 'RETRY',
          onActionClick: (): void => {
            sendInvoice(values);
          }
        },
        timeToShow: 5000,
        onClosed: () => setIsFirstTime(true)
      });
    } finally {
      handleClose();
    }
  };

  const closeWarning = (): void => {
    setIsFirstTime(true);
    setIsOpenWarning(false);
  };

  const validate = (values: FormikValues): void | FormikErrors<FormikValues> => {
    const [errors, errorInputName] = getErrorInfo(loadsRates, source);

    if (errorInputName) {
      const errorMessages = Object.values(errors);
      if (errorMessages && errorMessages.length > 0) {
        addNotification({
          message: errorMessages[0] as string,
          variant: 'error'
        });
      }
      scrollToInput(errorInputName);
    }
    return errors;
  };

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: initialInvoiceData,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    enableReinitialize: true,
    validate,
    onSubmit: (values) => {
      if (isBillFromChanged) {
        addNotification({
          message: `Save Billing information to proceed`,
          variant: 'error'
        });
        return;
      }

      sendInvoice(values);
    }
  });

  // Update sendInvoice value when accountingSettings are loaded
  useEffect(() => {
    if (accountingSettings && setFieldValue) {
      // Only update if we haven't already loaded data through getLoadsData
      // This prevents overriding user changes if they've manually toggled the option
      if (!loads.length) {
        setFieldValue(
          'sendInvoice',
          accountingSettings.select_invoice_send_option !== undefined
            ? accountingSettings.select_invoice_send_option
            : false
        );
      }
    }
  }, [accountingSettings, setFieldValue, loads]);

  const saveBillFrom = (): void => {
    setBillFromSelectedCompanyId(tempBillFromSelectedCompanyId);
    setFieldValue('billFromCompanyId', tempBillFromSelectedCompanyId);
  };

  const discardBillFrom = (): void => {
    setTempBillFromSelectedCompanyId(billFromSelectedCompanyId);
  };

  const generateAttachmentSizeWarningMessage = (): string => {
    if (!enableZipAttachments) {
      // if zip attachments are disabled, we can't send more than 35 MB
      return `Selected attachments exceed 35 MB. Please remove attachments or decrease file sizes to
            send load docs.`;
    }

    // if zip attachments are enabled, source is QBO and enableSendQboInvoiceInternally is disabled
    // we can't send more than 35 MB because qbo doesn't support zip attachments
    if (!enableSendQboInvoiceInternally && source === InvoiceSources.QBO) {
      return `Selected attachments exceed 35 MB. Please remove attachments or decrease file sizes to
            send load docs.`;
    }

    // if zip attachments are enabled and source is TB or enableSendQboInvoiceInternally is enabled
    // we can send up to 70 MB
    return `Selected attachments exceed ${maxAttachmentSize} MB. You can send all selected
            attachments as .zip archive or remove some attachments and send them separately.`;
  };

  const generateAttachmentSizeWarningButtonLabel = (): string => {
    if (!enableZipAttachments) {
      // if zip attachments are disabled, we can't send more than 35 MB
      return 'Okay';
    }

    // if zip attachments are enabled, source is QBO and enableSendQboInvoiceInternally is disabled
    // we can't send more than 35 MB because qbo doesn't support zip attachments
    if (!enableSendQboInvoiceInternally && source === InvoiceSources.QBO) {
      return 'Okay';
    }

    // if zip attachments are enabled and source is TB or enableSendQboInvoiceInternally is enabled
    // we can send up to 70 MB
    return 'Send as .zip';
  };

  const getInvoiceNumber = (): string => {
    const divisionPrefix = accountingSettings?.invoice_prefix;

    if (loads.length > 1 && source === InvoiceSources.TB && !divisionPrefix) {
      return `${loads[0]?.readable_id}-${loads.length}`;
    }

    if (source === InvoiceSources.QBO) {
      return `${qboInvoiceNumber}`;
    }

    if (!divisionPrefix) {
      return `${loads[0]?.readable_id}`;
    }

    return `${tbInvoiceNumber}`;
  };

  // #region handlers
  const handleConfirmAttachmentSizeWarningButtonAction = (): void => {
    if (!enableZipAttachments) {
      setAttachmentSizeWarningOpen(false);
      return;
    }

    if (!enableSendQboInvoiceInternally && source === InvoiceSources.QBO) {
      setAttachmentSizeWarningOpen(false);
      return;
    }

    setAttachmentSizeWarningOpen(false);
    setZipAttachments(true);
  };

  const handleBillingEmailChange = async (id: string): Promise<void> => {
    if (source === InvoiceSources.QBO && billFromSelectedCompanyId) {
      await qboCustomerSearch({ customerId: id, divisionId: billFromSelectedCompanyId })
        .unwrap()
        .then((qboCustomer) => {
          if (qboCustomer) {
            setBillToTooltipString(BILLING_EMAIL_FOUND_TEXT);
            setBillToTooltipType('success');
          } else {
            setBillToTooltipString(BILLING_EMAIL_NOT_FOUND_TEXT);
            setBillToTooltipType('warning');
          }
        })
        .catch((err) => {
          console.error('Error searching for customer in QBO: ', err);
        });
    }
  };

  const handleDocumentsUpload = async (): Promise<void> => {
    try {
      const allDocs = await LoadsService.getAllDocumentsByOrderOrTrip(loadIds);

      if (allDocs && allDocs.length > 0) {
        const invoicePhotos = allDocs.filter(
          (doc) =>
            doc.attachment.file_type.includes('image') && (!doc.type || LoadDocumentType.empty)
        );
        const onlyDocs = allDocs.filter(
          (doc) =>
            doc.type !== LoadDocumentType.empty || !doc.attachment.file_type.includes('image')
        );
        setFieldValue('documents', onlyDocs);
        setFieldValue('photos', invoicePhotos);
      }
    } catch (error) {
      addNotification({
        variant: 'warning',
        message:
          'Documents uploaded but failed to fetch them. Please refresh the page to see the documents.'
      });
    }
  };

  const handleSavePreview = async (): Promise<void> => {
    handlePreviewInvoiceClose(true);
    dispatch(setOrderId(loadIds[0]));

    if (isLoadDetailOpen) {
      dispatch(setLoadDetailReload(true));
    } else {
      dispatch(openLoadDetail());
      dispatch(setLoadDetailReload(true));
    }
    handleClose();
  };

  const handlePreviewInvoiceClose = (doCloseInvoicePage: boolean): void => {
    setInvoicePreviewOpen(false);
    if (doCloseInvoicePage) {
      handleClose();
      keepDetailOpen && dispatch(openLoadDetail());
    }
  };

  const handleQBOEdit = (invoiceId: string): void => {
    const division_id = divisionId;

    setInvoicePreviewOpen(false);
    handleClose();
    onEditQBOInvoice && onEditQBOInvoice(invoiceId, division_id);
  };

  const handleClose = (): void => {
    setShowQBOLoader(false);
    dispatch(setInvoiceLoadIds([]));
    dispatch(setInvoiceDivisionId(''));
    setLoads([]);
    setIsFirstTime(true);

    dispatch(closeCreateInvoice());
    setSource(quickbooksEnabled ? InvoiceSources.QBO : InvoiceSources.TB);
    setDueDate(getDate30DaysFromNow());
    setEmailsToSend([]);
    setEmailsToCc([]);
    setSelectedDocs([]);
    setSelectedPhotos([]);
    setInvoicePreviewAttachments([]);
    setOldClientEmail('');
    setIsOpenWarning(false);
    setPreviewBlob(undefined);
    onClickClose();
    setAdditionalQBOEmails([]);
    setInitialInvoiceData(INITIAL_INVOICE_DATA);
    setBillToTooltipString('');
    setBillToTooltipType('info');
    setTempBillFromSelectedCompanyId(undefined);
    setInvoiceId('');
    setQboInvoiceNumber(undefined);
    setTbInvoiceNumber(undefined);
  };

  const handleSelectedEmailsToSend = (emails: string[]): void => {
    setEmailsToSend(Array.from(new Set(emails)));
  };

  const handleSelectedEmailsToCc = (emails: string[]): void => {
    setEmailsToCc(Array.from(new Set(emails)));
  };

  const handleSelectedAdditionalEmails = (emails: string[]): void => {
    setAdditionalQBOEmails(emails);
  };

  // #region effects
  useEffect(() => {
    setSource(quickbooksEnabled ? InvoiceSources.QBO : InvoiceSources.TB);
  }, [quickbooksEnabled]);

  useEffect(() => {
    if (open) {
      setAddCustomerEmailToSend(
        (source === InvoiceSources.TB && !!toCustomerBilling) || source === InvoiceSources.QBO
      );
    }
  }, [open, toCustomerBilling, source]);

  useEffect(() => {
    open &&
      isFirstTime &&
      !!accountingSettings &&
      !!invoiceServicesData &&
      (async (): Promise<void> => {
        setIsLoading(true);
        return getLoadsData().finally(() => {
          setIsLoading(false);
          setIsFirstTime(false);
        });
      })();
  }, [open, accountingSettings, invoiceServicesData]);

  useEffect(() => {
    if (!accountingSettings) {
      setFactorInvoice(false);
      return;
    }

    setFactorInvoice(accountingSettings.send_invoices_to_factoring_by_default);
  }, [accountingSettings]);

  useEffect(() => {
    if (!enableSendQboInvoiceInternally) {
      setZipAttachments(false);
    }
  }, [enableSendQboInvoiceInternally]);

  useEffect(() => {
    if (billToData && loads[0]?.raw_customer) {
      let toCustomerContact;

      if (loads[0]?.raw_customer?.contact) {
        toCustomerContact = loads[0]?.raw_customer?.contact?.email_address;
      }
      updateEmailsToSend(
        billToData.clientEmail,
        toCustomerContact,
        loads[0]?.raw_customer?.preferences || undefined
      );
    }
  }, [billToData?.clientEmail, loads]);

  const getButtonLabel = useCallback(
    (values: CreateInvoiceFormValues): string => {
      if (factorInvoice && !isMultiLoadInvoice)
        return values.sendInvoice ? 'Preview' : 'Create & send to factoring';

      if (source === InvoiceSources.TB) {
        return values.sendInvoice ? 'Preview' : 'Save';
      }

      if (source === InvoiceSources.QBO && !enableSendQboInvoiceInternally) {
        return values.sendInvoice ? 'Create in Quickbooks + send' : 'Create in Quickbooks';
      }

      return 'Create & review';
    },
    [source, factorInvoice, isMultiLoadInvoice]
  );

  useEffect(() => {
    if (!divisionId || !enableFactoring) return;

    getFactoringConfig();
  }, [divisionId]);

  const getFactoringConfig = async (): Promise<void> => {
    FactoringService.getConfig(divisionId)
      .then((response) => {
        setFactoringCompanyName(response.company);
        setShowFactorInvoice(true);
      })
      .catch((error) => {
        if (error.status !== 404 && error.status !== 403) {
          addNotification({
            message: error.data
              ? error.data
              : "Something went wrong while getting factoring company's config",
            variant: 'error'
          });
        }

        if (error.status === 404 && fetchedInternalFactoring) {
          setFactoringCompanyName(fetchedInternalFactoring.name);
          setShowFactorInvoice(true);
          return;
        }

        setShowFactorInvoice(false);
      });
  };

  useEffect(() => {
    if (source && loads) {
      setInitialInvoiceData((invoiceData) => ({
        ...invoiceData,
        // Keep the existing sendInvoice value (from select_invoice_send_option)
        // instead of setting it based on source
        invoiceItems: getDefaultLineItems(loads)
      }));
    }
  }, [source, loads]);

  useEffect(() => {
    // Handling BOL check for single load invoice only for now
    if (isRequireBolActive && loads && loads.length == 1 && loads[0].bol_missing)
      dispatch(openUploadBol());
  }, [loads]);

  useEffect(() => {
    if (zipAttachments) {
      setMaxAttachmentSize(35); // setting this as we havent handle image compression yet
    } else {
      setMaxAttachmentSize(35);
    }
  }, [zipAttachments]);

  useEffect(() => {
    source === InvoiceSources.QBO &&
      setEmailsToSend(
        Array.from(
          new Set([
            ...additionalQBOEmails,
            ...(billToData?.clientEmail ? [billToData?.clientEmail] : [])
          ])
        )
      );
  }, [billToData?.clientEmail, additionalQBOEmails, source]);

  useEffect(() => {
    (async (): Promise<void> => {
      let selectedCompanyId = undefined;
      const { companyIds, carriers } = getCarriersAndCompanyIds(loads, userData) || [];
      loads.map((load) => {
        if (load.division_id) {
          companyIds.push(load.division_id);
          selectedCompanyId = load.division_id;
        }
      });

      setBillFromSelectedCompanyId(selectedCompanyId);
      setTempBillFromSelectedCompanyId(selectedCompanyId);
      setFieldValue('billFromCompanyId', selectedCompanyId);
      setBillFromCompanyIds(companyIds);
      setCarriers(carriers);
    })();
  }, [JSON.stringify(loads.map((l) => l.id))]);

  const [isInvoiceDisabled, setIsInvoiceDisabled] = useState(false);
  useEffect(() => {
    if (loads && loads.length > 0) {
      const load = loads[0];

      if (!load.qbo_attributes?.division_qbo_credentials_id) {
        // load is created with truckbase services
        setIsInvoiceDisabled(false);
        return;
      }

      if (
        load.qbo_attributes?.division_qbo_credentials_id !=
        accountingSettings?.division_qbo_credentials?.id
      ) {
        setIsInvoiceDisabled(true);
      } else {
        setIsInvoiceDisabled(false);
      }
    }
  }, [JSON.stringify(loads.map((l) => l.id)), accountingSettings?.division_qbo_credentials?.id]);

  const qboAlertMessage = useMemo<string>((): string => {
    return "The invoice is associated with a QuickBooks account that has been disconnected. Please go back to 'edit load' page to update the rates and services there.";
  }, []);

  const qboAlertActionLabel = useMemo<string>((): string => {
    return 'EDIT LOAD';
  }, []);

  const handleQboAlertAction = (): void => {
    if (loadIds && loadIds.length > 0) {
      dispatch(setOrderId(loadIds[0]));
      dispatch(openEditLoad());
      onClickClose();
    }
  };

  const ordersListForUpload = useMemo(() => {
    let ordersList: TwSelectOption[] = [];
    if (loads) {
      ordersList = loads.map((load) => {
        return {
          value: load.id,
          label: load.readable_id as unknown as string
        };
      });
    }
    return ordersList;
  }, [loads]);

  const handleFactorInvoiceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFactorInvoice(event.target.checked);
  };

  // #region sales tax

  const isSalesTaxEnabled = !!accountingSettings?.sales_tax_enabled;

  const [salesTaxOptions, setSalesTaxOptions] = useState<SalesTax[]>([]);
  const [isSalesTaxSelectVisible, setIsSalesTaxSelectVisible] = useState(false);
  const [isSalesTaxCreateUpdateModalOpen, setIsSalesTaxCreateUpdateModalOpen] = useState(false);
  const [taxAmount, setTaxAmount] = useState(0);
  const [totalWithTax, setTotalWithTax] = useState(0);
  const [salesTax, setSalesTax] = useState<any | null>(null);

  const getTotal = (loadsRates: { [key: string]: RowData[] }): number => {
    const flatRates = Object.values(loadsRates || {})
      .map((loadRates) => loadRates)
      .flat();
    const totalBalance = flatRates.reduce((acc, r) => {
      if (r.unit === InvoiceServiceUnit.FLAT) {
        return acc + (r.rate || 0);
      }
      let rate = r.rate;
      if (r.unit === InvoiceServiceUnit.PERCENTAGE) {
        rate = (r.rate || 0) / 100;
      }
      return acc + NumberUtils.calculateCurrencyAmount(rate, r.quantity);
    }, 0);

    return totalBalance;
  };

  const onAddSalesTaxClick = (): void => {
    setIsSalesTaxCreateUpdateModalOpen(true);
  };

  const refetchSalesTaxSettings = (): void => {
    SalesTaxService.getSalesTaxSettings(
      divisionId,
      { sort: 'name', orderBy: 'ASC' },
      { is_active: true }
    ).then((settings) => {
      setSalesTaxOptions(settings);
    });
  };

  useEffect(() => {
    divisionId && refetchSalesTaxSettings();
  }, [divisionId]);

  useEffect(() => {
    if (loadsRates) {
      const total = getTotal(loadsRates);
      const taxAmount = (total * (salesTax?.value || 0)) / 100;

      setTaxAmount(NumberUtils.roundNumber(taxAmount));
      setTotalWithTax(NumberUtils.roundNumber(total + taxAmount));
    }
  }, [loadsRates, salesTax]);

  // #endregion sales tax

  return (
    <>
      <TwDialog
        open={open}
        onClickClose={(): void => setIsOpenWarning(true)}
        title={
          clientData?.companyName
            ? `${source === InvoiceSources.QBO ? 'Create invoice for ' : 'Send invoice to'} ${
                clientData?.companyName
              }`
            : `${source === InvoiceSources.QBO ? 'Create' : 'Send'} invoice`
        }
        dialogTitleContent={quickbooksEnabled && source === InvoiceSources.QBO ? <QBOIcon /> : ''}
        fullScreen={true}
      >
        {isLoading ||
        areAccountingSettingsFetching ||
        areInvoiceServicesFetching ||
        areAccountingSettingsLoading ||
        areInvoiceServicesLoading ? (
          <div className="loading">
            <CircularProgress color="primary" />
          </div>
        ) : (
          <>
            {isInvoiceDisabled && (
              <TwAlert
                message={qboAlertMessage}
                severity="warning"
                icon={<AlertIcon />}
                sx={[
                  {
                    '& .MuiAlert-icon': {
                      padding: '8px 0 0px'
                    },
                    '& .MuiAlert-action': {
                      padding: '0px 0px'
                    }
                  },
                  {
                    backgroundColor: '#F3F4F6',
                    border: '1px solid #E5E7EB',
                    marginBottom: '30px'
                  }
                ]}
                action={
                  <TwButton
                    type={TwButtonType.text}
                    label={qboAlertActionLabel}
                    action={handleQboAlertAction}
                  />
                }
              />
            )}
            <form className={'create-tb-invoice'}>
              <span className="create-invoice__title">Add invoice details</span>
              <span className="create-invoice__qbo-message">
                Provide additional information to create an invoice in {source || values.source}.
                The invoice will be added to load card.
              </span>

              {source === InvoiceSources.TB && (
                <BillFrom
                  onBillFromEdited={saveBillFromInfo}
                  companyIds={billFromCompanyIds}
                  selectedCompanyId={tempBillFromSelectedCompanyId}
                  onSelectedCompanyChanged={(companyId: string | undefined): void => {
                    setTempBillFromSelectedCompanyId(companyId);
                  }}
                  saveBillFrom={saveBillFrom}
                  discardBillFrom={discardBillFrom}
                  isBillFromChanged={isBillFromChanged}
                  updateCompanyIds={(companyIds: string[], selectedId): void => {
                    setBillFromCompanyIds(companyIds);
                    setTempBillFromSelectedCompanyId(selectedId);
                  }}
                  carriers={carriers}
                  tenantAccountingSettings={accountingSettings}
                />
              )}

              {source === InvoiceSources.QBO && (
                <BillFromQBO
                  companyName={accountingSettings?.division_qbo_credentials?.company_name}
                />
              )}

              <BillTo
                clientId={clientData?.companyId ? clientData?.companyId : ''}
                onBillToEdited={(billToInfo: BillToData): void => {
                  // We set previous saved email in billToData as old client email
                  setOldClientEmail(billToData?.clientEmail || '');
                  saveBillToInfo(billToInfo);
                }}
                onBillingEmailChange={handleBillingEmailChange}
                tooltipString={billToTooltipString}
                tooltipType={billToTooltipType}
              />

              {/* TODO: This is commented out until we have a way to handle the invoice prefix */}
              {/* {source === InvoiceSources.TB && !isMultiLoadInvoice && (
                <TwField
                  id="invoicePrefix"
                  name="invoicePrefix"
                  label={
                    <Box display="flex" alignItems="center" gap="4px">
                      Invoice number
                      <TwTooltip
                        title="Invoice number will be generated after the invoice is created and/or sent out"
                        placement="top"
                      >
                        <InfoIcon
                          sx={{
                            fill: '#D1D5DB',
                            height: '15px',
                            width: '15px'
                          }}
                        />
                      </TwTooltip>
                    </Box>
                  }
                  inputType={TwInputTypes.text}
                  value={
                    accountingSettings?.invoice_prefix !== null
                      ? `${accountingSettings?.invoice_prefix}-[INVOICE_NUMBER]`
                      : '[INVOICE_NUMBER]'
                  }
                  disabled={true}
                  className="create-invoice__two-column-item"
                />
              )} */}

              {enableFactoring && showFactorInvoice && (
                <FactorInvoice
                  isMultiLoadInvoice={isMultiLoadInvoice}
                  factorInvoice={factorInvoice}
                  factoringCompany={factoringCompanyName || '-'}
                  handleFactorInvoiceChange={handleFactorInvoiceChange}
                />
              )}

              <LoadsRates
                loads={loads}
                loadsRates={loadsRates}
                divisionId={divisionId}
                onUpdateLoadRates={(data: RowData[], loadId: string): void => {
                  const getInvoiceDefaultDescription = invoiceDefaultDescriptionMaker(
                    accountingSettings!
                  );

                  setLoadsRates((prev) => {
                    const newData = data.map((row) => {
                      // set default description for new added row
                      if (!('description' in row)) {
                        return {
                          ...row,
                          description: getInvoiceDefaultDescription(
                            loads.find((l) => l.id === loadId) || loads[0]
                          )
                        };
                      }
                      return row;
                    });
                    if (prev) {
                      return { ...prev, [loadId]: newData };
                    }
                  });
                }}
                disabled={isInvoiceDisabled}
              />

              {isSalesTaxEnabled && (
                <>
                  <SalesTaxCreateUpdateModal
                    open={isSalesTaxCreateUpdateModalOpen}
                    onClose={(): void => {
                      setIsSalesTaxCreateUpdateModalOpen(false);
                      refetchSalesTaxSettings();
                    }}
                    divisionId={divisionId}
                  />
                  <Grid container position="relative" marginTop="10px">
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          cursor: 'pointer',
                          color: '#3A4DE9',
                          fontWeight: 500,
                          fontSize: '14px',
                          width: '100px',
                          display: isSalesTaxSelectVisible ? 'none' : 'inherit'
                        }}
                        onClick={(): void => {
                          setIsSalesTaxSelectVisible(true);
                        }}
                      >
                        + Add tax
                      </Box>
                      <Box display={isSalesTaxSelectVisible ? 'inherit' : 'none'}>
                        <TwAutocomplete
                          value={salesTax}
                          placeholder="Select tax..."
                          optionList={salesTaxOptions.map((tax) => ({
                            id: tax.id,
                            label: tax.name || '',
                            value: tax.rate || 0
                          }))}
                          onChange={(e, newValue): void => {
                            setSalesTax(newValue);
                          }}
                          name="salesTax"
                          sx={{ width: '200px' }}
                          listAction={{
                            label: 'New Tax',
                            action: onAddSalesTaxClick
                          }}
                          disableClearable={true}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Grid container>
                        {!salesTax ? (
                          <Grid
                            item
                            xs={12}
                            display={isSalesTaxSelectVisible ? 'flex' : 'none'}
                            alignItems="center"
                            justifyContent="flex-end"
                            paddingRight="33px"
                            paddingTop="7px"
                            paddingBottom="15px"
                          >
                            <Typography color="#9CA3AF" fontSize="14px">
                              Select tax to calculate
                            </Typography>
                          </Grid>
                        ) : (
                          <>
                            <Grid
                              item
                              xs={7.7}
                              display={isSalesTaxSelectVisible ? 'flex' : 'none'}
                              justifyContent="flex-end"
                              alignItems="center"
                              paddingTop="8px"
                              paddingBottom="14px"
                            >
                              <Box
                                component="div"
                                sx={{
                                  fontFamily: 'Poppins',
                                  fontWeight: 600,
                                  lineHeight: '21px',
                                  letterSpacing: 0,
                                  fontSize: '14px',
                                  color: '#9CA3AF'
                                }}
                              >
                                {salesTax?.value || 0} %
                              </Box>
                            </Grid>
                            <Grid
                              item
                              xs={4.3}
                              display={isSalesTaxSelectVisible ? 'flex' : 'none'}
                              alignItems="center"
                              paddingTop="8px"
                              paddingBottom="14px"
                              justifyContent="flex-end"
                              paddingRight="35px"
                            >
                              <Box component="div" fontSize="14px" marginLeft="8px">
                                <Box component="span" color="#9CA3AF">
                                  ${' '}
                                </Box>
                                {taxAmount === 0
                                  ? 0
                                  : NumberUtils.getCurrency(taxAmount).replace('$', '')}
                              </Box>
                            </Grid>
                          </>
                        )}
                        {/* TOTAL */}
                        <Grid
                          item
                          xs={7.7}
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <Box
                            component="div"
                            sx={{
                              fontFamily: 'Poppins',
                              fontWeight: 600,
                              lineHeight: '21px',
                              letterSpacing: 0,
                              fontSize: '14px',
                              color: '#9CA3AF'
                            }}
                          >
                            {loads.length > 1 ? 'GRAND TOTAL' : 'TOTAL'}
                          </Box>
                        </Grid>
                        <Grid
                          item
                          xs={4.3}
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                        >
                          <Box component="div" fontSize="14px" marginLeft="8px" paddingRight="35px">
                            <Box component="span" color="#9CA3AF">
                              ${' '}
                            </Box>
                            {totalWithTax === 0
                              ? 0
                              : NumberUtils.getCurrency(totalWithTax).replace('$', '')}
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>

                    <IconButton
                      onClick={(): void => {
                        setSalesTax(null);
                        setIsSalesTaxSelectVisible(false);
                        setFieldValue('salesTaxId', '');
                        setFieldValue('salesTaxRate', 0);
                      }}
                      sx={{
                        position: 'absolute',
                        top: '0',
                        right: '0',
                        display: isSalesTaxSelectVisible ? 'flex' : 'none'
                      }}
                    >
                      <CloseIcon
                        sx={{
                          width: '20px',
                          height: '20px'
                        }}
                      />
                    </IconButton>
                  </Grid>
                </>
              )}

              {loads && !isLoading && (
                <>
                  <span className="create-invoice__section-title">
                    Select documents you want to attach:
                  </span>
                  <TwSelectableDocsPhotos
                    photos={!isMultiLoadInvoice && values.photos ? values.photos : []}
                    documents={values.documents}
                    onSelectDocuments={setSelectedDocs}
                    onSelectPhotos={setSelectedPhotos}
                    showUploadButton={!isMultiLoadInvoice}
                    onUploadButtonClick={(): void => {
                      setDocumentsOpen(true);
                    }}
                    onDocumentClick={(document): void => {
                      setFilesToView(values.documents);
                      setSelectedFileToView(document);
                      setDocsViewerOpen(true);
                    }}
                    onPhotoClick={(photo): void => {
                      setFilesToView(values.photos);
                      setSelectedFileToView(photo);
                      setDocsViewerOpen(true);
                    }}
                    disableEncryptedDocuments={mergeAttachments}
                  />
                </>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '1rem'
                }}
              >
                <TwCheckbox
                  label="Merge attachments"
                  name="mergeAttachments"
                  onInputChange={(): void => {
                    setMergeAttachments((prev: any) => !prev);
                  }}
                  checked={mergeAttachments}
                />

                {(source === InvoiceSources.TB ||
                  (source === InvoiceSources.QBO && enableSendQboInvoiceInternally)) &&
                  enableZipAttachments && (
                    <TwCheckbox
                      label="Zip attachments"
                      name="zipAttachments"
                      onInputChange={(): void => {
                        setZipAttachments((prev: any) => !prev);
                      }}
                      checked={zipAttachments}
                    />
                  )}
              </Box>

              {source === InvoiceSources.TB && (
                <TwField
                  id="specialInstructions"
                  name="specialInstructions"
                  label="Notes (optional)"
                  inputType={TwInputTypes.textarea}
                  value={values.specialInstructions}
                  onInputChange={handleChange}
                  className="create-invoice__instructions"
                />
              )}

              {source === InvoiceSources.QBO && qboPreferences.classTrackingPerTxn === true && (
                <Box>
                  <TwSelect
                    label="Class"
                    value={values.qboClassId}
                    name="qboClassId"
                    optionList={classesDropdown}
                    onInputChange={handleChange}
                    className="create-invoice__qbo-class__select"
                    labelIcon={
                      <TwTooltip
                        title="Class selection is being pulled from QBO account"
                        placement="top"
                      >
                        <InfoIcon className="create-invoice__qbo-class__icon" />
                      </TwTooltip>
                    }
                  />
                </Box>
              )}

              <div
                className={
                  source === InvoiceSources.QBO
                    ? 'create-invoice__qbo-payment-terms'
                    : 'create-invoice__payment-terms '
                }
              >
                <TwSelect
                  label="Payment terms"
                  value={values.paymentTerm}
                  name="paymentTerm"
                  optionList={PAYMENT_TERMS}
                  onInputChange={(e): void => {
                    handleChange(e);
                    setFieldValue('customDueDate', null);

                    if (e.target.value !== DefaultPaymentTerms.OTHER) {
                      setDueDate(
                        getDueDateFromSetting(
                          loads,
                          InvoiceUtils.getDaysFromPaymentTerms(
                            e.target.value as DefaultPaymentTerms
                          )
                        )
                      );
                    }
                  }}
                  className="create-invoice__payment-terms-select"
                />
                <div className="create-invoice__due-date-section">
                  <span className="create-invoice__field-name">Due date</span>
                  {values.paymentTerm !== DefaultPaymentTerms.OTHER ? (
                    <div className="create-invoice__due-date">
                      <CalendarTodayIcon className="create-invoice__due-date-calendar" />
                      <div className="create-invoice__due-date-day">
                        {dueDate && DateUtils.getMediumDate(dueDate)}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <TwDatePicker
                        id="custom-due-date"
                        name="customDueDate"
                        value={values.customDueDate}
                        placeholder="MM/DD/YY"
                        onChange={(newDate): void => {
                          const date = newDate as Date | null;

                          !!date && setDueDate(date);

                          setFieldValue('customDueDate', date);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="create-invoice__footer">
                {quickbooksEnabled &&
                  source === InvoiceSources.QBO &&
                  !enableSendQboInvoiceInternally && (
                    <div className="create-invoice__footer-options">
                      <div style={{ display: 'flex' }}>
                        <TwCheckbox
                          label="Send"
                          name="sendInvoice"
                          onInputChange={handleChange}
                          value={values.sendInvoice}
                          checked={values.sendInvoice}
                        />
                        <TwTooltip
                          title="Mark send invoice directly to customer and additional recipients, and transfer data to Quickbooks"
                          placement="top"
                        >
                          <InfoIcon
                            className="create-invoice__info"
                            sx={{
                              fill: '#D1D5DB',
                              height: '18px',
                              width: '18px'
                            }}
                          />
                        </TwTooltip>
                      </div>
                      {values.sendInvoice && (
                        <>
                          <TwTagsField
                            id="emailListQBO"
                            label="To"
                            tagsDisplayed={emailsToSendQBO}
                            tagType={'email'}
                            suggestedOptions={suggestedEmails}
                            disabled
                          />

                          <TwTagsField
                            id="emailListQBOAdditionalRecipients"
                            label="Add additional recipients"
                            tagsDisplayed={additionalQBOEmails}
                            tagType={'email'}
                            onTagAdded={(emails): void => {
                              handleSelectedAdditionalEmails(emails);
                            }}
                            suggestedOptions={suggestedEmails}
                            placeholder="Optional"
                          />
                        </>
                      )}
                    </div>
                  )}

                {source === InvoiceSources.TB && (
                  <div className="create-invoice__footer-options">
                    <Box display={'flex'}>
                      <TwCheckbox
                        label="Send invoice"
                        name="sendInvoice"
                        onInputChange={handleChange}
                        value={values.sendInvoice}
                        checked={values.sendInvoice}
                      />
                      <TwTooltip
                        title="Mark checkbox to auto-fill recipient emails based on invoice settings, and edit email fields"
                        placement="top"
                      >
                        <InfoOutlinedIcon className="create-invoice__info" />
                      </TwTooltip>
                    </Box>
                  </div>
                )}

                <div
                  className={
                    source === InvoiceSources.QBO
                      ? 'create-invoice__footer-qbo-actions'
                      : 'create-invoice__footer-tb-actions'
                  }
                >
                  <TwButton
                    fullwidth={false}
                    label="Cancel"
                    type={TwButtonType.secondary}
                    action={(): void => setIsOpenWarning(true)}
                  />
                  <TwButton
                    fullwidth={false}
                    label={getButtonLabel(values)}
                    type={TwButtonType.primary}
                    action={handleSubmit}
                    disabled={isInvoiceDisabled}
                  />
                </div>
              </div>
            </form>
          </>
        )}
      </TwDialog>

      <TwDialog open={isOpenWarning} title="Leave page?" onClickClose={closeWarning}>
        <div className="warning">
          <Warning />
          <p className="warning__message">
            You haven’t finished creating an invoice.
            <br /> Do you want to leave without finishing?
          </p>
          <div className="warning__actions">
            <TwButton
              fullwidth={false}
              label="Close without saving"
              type={TwButtonType.secondary}
              action={handleClose}
            />
            <TwButton
              fullwidth={false}
              label="Continue editing"
              type={TwButtonType.primary}
              action={closeWarning}
            />
          </div>
        </div>
      </TwDialog>

      <UploadBol
        loadId={loadIds[0]}
        open={isUploadBolOpen}
        onClose={(): void => {
          dispatch(closeUploadBol());
        }}
        onSuccess={(): void => {
          (async (): Promise<void> => {
            return getLoadsData();
          })();
        }}
      />

      <TwDialog
        open={attachmentSizeWarningOpen}
        title="Unable to send invoice"
        onClickClose={(): void => setAttachmentSizeWarningOpen(false)}
      >
        <div className="warning">
          <Warning />
          <span className="warning__message">{generateAttachmentSizeWarningMessage()}</span>
          <div className="warning__actions">
            <TwButton
              fullwidth={false}
              label={generateAttachmentSizeWarningButtonLabel()}
              type={TwButtonType.primary}
              action={handleConfirmAttachmentSizeWarningButtonAction}
            />
          </div>
        </div>
      </TwDialog>

      <TwDialog
        open={zipAttachmentSizeWarningOpen}
        title="Zip file too large to send invoice"
        onClickClose={(): void => setZipAttachmentSizeWarningOpen(false)}
      >
        <div className="warning">
          <Warning />
          <span className="warning__message">
            Your zipped attachments have exceeded the size limit of {maxAttachmentSize} MB for
            sending an invoice. Please decrease the file sizes or deselect some attachements.
          </span>
          <div className="warning__actions">
            <TwButton
              fullwidth={false}
              label="Got it"
              type={TwButtonType.primary}
              action={(): void => setZipAttachmentSizeWarningOpen(false)}
            />
          </div>
        </div>
      </TwDialog>

      <TwUploadOrderTripFiles
        open={documentsOpen}
        handleClose={(): void => setDocumentsOpen(false)}
        onAddDocumentsSuccess={handleDocumentsUpload}
        defaultVisibility={isEnabledDriversSeeDocs}
        entity="order"
        disableInternalUpload={false}
        orderId={loads[0]?.id}
        entityList={ordersListForUpload}
        hideEntitySelector={true}
      />

      {/** Document and photo preview */}
      {loads && loads.length > 0 && loads[0].order_attachments && (
        <TwOrderTripsDocViewer
          readableId={loads[0]?.readable_id}
          files={filesToView}
          open={docsViewerOpen}
          onClickClose={(): void => setDocsViewerOpen(false)}
          selectedFileId={selectedFileToView.id}
        />
      )}

      <TwDocumentViewer
        readableId={readableId}
        open={invoiceViewerOpen}
        blobTitle={blobTitle}
        downloadName={downloadName}
        useBlobTitleAsFileName
        onClickClose={(): void => {
          setInvoiceViewerOpen(false);
          setBlob(undefined);
        }}
        blob={blob}
        blobType={TwDocumentType.pdf}
        files={null}
      />

      {invoicePreviewOpen && (
        <PreviewInvoice
          open={invoicePreviewOpen}
          source={source}
          invoiceId={invoiceId}
          invoiceDialogClosed={false}
          attachments={invoicePreviewAttachments}
          loading={invoicePreviewLoading}
          onClose={(doCloseInvoicePage: boolean): void => {
            handlePreviewInvoiceClose(doCloseInvoicePage);

            if (source === InvoiceSources.QBO) {
              handleClose();
            }
          }}
          onSaveClick={handleSavePreview}
          suggestedContacts={suggestedEmails}
          onSelectedEmailTo={(emails): void => handleSelectedEmailsToSend(emails)}
          onSelectedEmailCc={(emails): void => handleSelectedEmailsToCc(emails)}
          onSelectedAdditionalEmailsChange={(emails): void =>
            handleSelectedAdditionalEmails(emails)
          }
          presetEmailTo={emailsToSend}
          presetEmailCc={emailsToCc}
          presetQboEmailTo={emailsToSendQBO}
          presetAdditionalQBOEmails={additionalQBOEmails}
          customerName={billToData?.clientName}
          tenantData={{
            userName: `${userData?.first_name} ${userData?.last_name}`,
            companyName: userData?.company_name || ''
          }}
          invoiceNumber={getInvoiceNumber()}
          customerOrderNumber={
            loads[0]?.customer_order_number
              ? loads.length > 1
                ? `${loads[0].customer_order_number}-${loads.length}`
                : loads[0].customer_order_number
              : ''
          }
          onInvoiceSent={(previewData: PreviewInvoiceSendData): void => {
            createInvoice(values, previewData);
          }}
          onQBOInvoiceSent={sendQBOInvoice}
          onEditClick={handleQBOEdit}
          onInvoiceDeleted={handleClose}
          canSendInvoice={(): boolean => {
            return true;
          }}
          tenantAccountingSettings={accountingSettings}
          divisionId={divisionId}
          factorInvoice={factorInvoice}
        />
      )}

      {/** Invoice PDF viewer for Truckbase invoice preview page */}
      <TwDocumentViewer
        disableDownload={
          // we only want to allow download if we're not using invoice prefix and id feature
          !!accountingSettings?.invoice_prefix
        }
        readableId={readableId}
        blob={previewBlob}
        files={null}
        blobType={TwDocumentType.pdf}
        blobTitle={InvoiceUtils.getInvoiceBlobTitle(getInvoiceNumber())}
        open={invoicePreviewOpen}
        hideCloseIcon={source === InvoiceSources.QBO && enableSendQboInvoiceInternally}
        onClickClose={(): void => handlePreviewInvoiceClose(false)}
        className="create-invoice__preview-invoice-viewer"
        PaperProps={{
          sx: {
            position: 'absolute',
            width: '68%',
            top: 0,
            right: 0,
            margin: 0,
            height: '100vh',
            maxHeight: '100vh',
            zIndex: '1400'
          }
        }}
      />

      <TwQBOLoader open={showQBOLoader} />
    </>
  );
};

export default CreateInvoice;
